/** Admin → AI models (SA-1087 §12, D15; SA-1103): every non-persona LLM function with its
 * provider, model, effort, key and where the value comes from, a vision function's last failures,
 * and Edit for a holder of Manage AI Models. */
import { useState } from 'react'
import { Alert, AlertDescription, Badge, Button, Spinner } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useAiModels } from '@/hooks/useAiModels'
import { usePermission } from '@/hooks/usePermission'
import type { AiModelFunction, AiModelSource, AiModelsRead } from '@/types/aiModels'
import { AiModelDialog } from './AiModelDialog'
import * as text from './aiModelsText'

const TONE: Record<AiModelSource, 'success-soft' | 'info-soft' | 'muted-soft'> = {
  page: 'success-soft', env: 'info-soft', copilot_config: 'info-soft', doc_assistant: 'info-soft',
  persona: 'info-soft', unconfigured: 'muted-soft',
}

function Failures({ fn }: { fn: AiModelFunction }) {
  const shown = fn.last_failures.slice(0, text.FAILURES_SHOWN)
  const more = fn.last_failures.length - shown.length
  if (shown.length === 0) return null
  return (
    <div className="space-y-0.5">
      {shown.map((f, i) => <p key={i} className="text-xs text-destructive">{text.failureLine(f)}</p>)}
      {more > 0 && <p className="text-xs text-destructive">{text.moreFailures(more)}</p>}
    </div>
  )
}

function SourceCell({ fn }: { fn: AiModelFunction }) {
  const by = text.savedByLine(fn)
  return (
    <div className="space-y-0.5">
      <Badge variant={TONE[fn.source]}>{text.SOURCE_WORDS[fn.source]}</Badge>
      {fn.source !== 'page' && fn.source !== 'persona' && fn.source_detail && (
        <p className="text-xs text-muted-foreground">{fn.source_detail}</p>
      )}
      {by && <p className="text-xs text-muted-foreground">{by}</p>}
      {fn.unsupported_provider && <p className="text-xs text-destructive">{text.UNSUPPORTED}</p>}
      <Failures fn={fn} />
    </div>
  )
}

function Row({ fn, read, canManage, onEdit }: {
  fn: AiModelFunction; read: AiModelsRead; canManage: boolean; onEdit: (fn: AiModelFunction) => void
}) {
  const e = fn.effective
  const providerLabel = e ? (read.providers.find((p) => p.id === e.provider)?.label ?? e.provider) : text.DASH
  const recommended = text.recommendedLine(fn)
  return (
    <tr aria-label={fn.label} className="border-t border-border align-top">
      <td className="px-2.5 py-2 font-medium">{fn.label}</td>
      <td className="px-2.5 py-2">{providerLabel}</td>
      <td className="px-2.5 py-2">
        <div>{e?.model ?? text.DASH}</div>
        {!fn.saved && recommended && <p className="text-xs text-muted-foreground">{recommended}</p>}
      </td>
      <td className="px-2.5 py-2">{e ? (e.effort ?? text.MODEL_DEFAULT_EFFORT) : text.DASH}</td>
      <td className="px-2.5 py-2">{text.keyWords(fn)}</td>
      <td className="px-2.5 py-2"><SourceCell fn={fn} /></td>
      <td className="px-2.5 py-2 text-right">
        {canManage && (
          <Button variant="outline" size="sm" aria-label={text.editLabel(fn)} onClick={() => onEdit(fn)}>{text.EDIT}</Button>
        )}
      </td>
    </tr>
  )
}

export function AiModelsPage() {
  const read = useAiModels()
  const canManage = usePermission('ai_models:manage')
  const [editing, setEditing] = useState<AiModelFunction | null>(null)
  const editingId = editing?.id
  const data = read.data
  const groups = data ? Array.from(new Set(data.functions.map((f) => f.group))) : []
  const unreadable = data?.unreadable_labels ?? []
  return (
    <PageFrame width="detail" className="space-y-6">
      <PageHeader title={text.PAGE_TITLE} subtitle={text.PAGE_SENTENCE} />
      {unreadable.length > 0 && (
        <Alert variant="warning"><AlertDescription>{text.unreadableLine(unreadable)}</AlertDescription></Alert>
      )}
      {data === undefined ? (
        read.isError
          ? <Alert variant="destructive"><AlertDescription>{text.UNAVAILABLE}</AlertDescription></Alert>
          : <div className="flex justify-center py-6"><Spinner /></div>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              {text.COLUMNS.map((c) => <th key={c} scope="col" className="px-2.5 py-2">{c}</th>)}
              <th scope="col" className="px-2.5 py-2"><span className="sr-only">{text.ACTIONS}</span></th>
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g}>
              <tr><th scope="rowgroup" colSpan={text.COLUMNS.length + 1} className="bg-muted/40 px-2.5 py-1.5 text-left text-xs font-semibold">{g}</th></tr>
              {data.functions.filter((f) => f.group === g).map((f) => (
                <Row key={f.id} fn={f} read={data} canManage={canManage} onEdit={setEditing} />
              ))}
            </tbody>
          ))}
        </table>
      )}
      {/* A dialog closes only the function it was opened for: a save that lands after
          Cancel must never close the next function's dialog and drop its typing. */}
      {data && editing && canManage && (
        <AiModelDialog
          key={editing.id} fn={data.functions.find((f) => f.id === editing.id) ?? editing} read={data}
          onClose={() => setEditing((cur) => (cur?.id === editingId ? null : cur))}
        />
      )}
    </PageFrame>
  )
}
